import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import type { PremiseConfig } from "@shared/types/foyer";
import { foyerApi } from "../http/foyer";
import { usePremise } from "../hooks/usePremise";
import { useScope } from "./ScopeContext";
import { useTranslation } from "react-i18next";
import { describeError, useToast, type ActionKey } from "../components/toast";

/**
 * The premise currently being worked on, loaded from the header's Location
 * scope rather than from the URL.
 *
 * This is the hinge of the estate-first IA: pages no longer live inside one
 * premise's editor, they live under a product area and read whichever premise
 * the header points at.
 *
 * ## There is no draft
 *
 * `premise` is the premise AS SAVED. Nothing edits it in place and there is no
 * page-level "Unsaved changes + Save": every change is written the moment it is
 * made, from a drawer's Save, a modal's Create or an instant control, and each
 * write is toasted. A page that needs to hold half-typed input keeps it in its
 * own drawer/modal and writes it with {@link PremiseWorkspaceState.commit}.
 * (Why: a shared draft let one tab's Save write another tab's half-finished
 * edit, and let the "unsaved" chip outlive a save that had succeeded.)
 */

/** What to tell the user about one {@link PremiseCommit}. */
export interface PremiseCommitMessages {
  /** Shown on success, e.g. "Opening hours for Grand updated." */
  success: string;
  /** The action that failed, as a key under `actions` in `common`. */
  failure: ActionKey;
  failureParams?: Record<string, string | number>;
}

/**
 * Persist ONE change to the premise right now and tell the user.
 *
 * `update` is applied to the last SAVED config, so a change computed from a
 * stale render still lands on top of whatever was saved since. Commits run one
 * at a time: each reads the version the previous one wrote, which is what keeps
 * two quick edits from clobbering each other or colliding on `version`.
 *
 * Resolves true on success. A failure toasts and resolves false; nothing is
 * changed on screen.
 */
export type PremiseCommit = (
  update: (current: PremiseConfig) => PremiseConfig,
  messages: PremiseCommitMessages
) => Promise<boolean>;

export interface PremiseWorkspaceState {
  /** Slug in scope, or "" when the header is set to all locations. */
  premiseId: string;
  /** The premise in scope, as last saved. Null while loading or unscoped. */
  premise: PremiseConfig | null;
  loading: boolean;
  error: Error | null;
  /** Persist one change immediately and toast the outcome. */
  commit: PremiseCommit;
  /**
   * Mirror a change the SERVER already holds (a room checked in through its
   * own endpoint, say) into the saved config. Writes nothing and toasts
   * nothing; it only keeps this copy equal to what is stored.
   */
  applyServerChange: (
    update: (current: PremiseConfig) => PremiseConfig
  ) => void;
}

const Ctx = createContext<PremiseWorkspaceState | null>(null);

export function PremiseWorkspaceProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { scope } = useScope();
  const premiseId = scope.premiseId;
  const { config, loading, error, applySaved } = usePremise(premiseId);
  const toast = useToast();
  const { t } = useTranslation();

  // The saved config as of the latest write, readable from a commit that was
  // queued before React re-rendered with the previous commit's result.
  const configRef = useRef<PremiseConfig | null>(null);
  useEffect(() => {
    configRef.current = config;
  }, [config]);
  // The premise in scope NOW, so a write that finishes after the header was
  // pointed somewhere else cannot overwrite the new premise's copy.
  const premiseIdRef = useRef(premiseId);
  premiseIdRef.current = premiseId;
  const commitQueue = useRef<Promise<unknown>>(Promise.resolve());

  const commit = useCallback<PremiseCommit>(
    (update, messages) => {
      const run = async (): Promise<boolean> => {
        const base = configRef.current;
        if (!premiseId || !base || base.premiseId !== premiseId) {
          toast.error(
            describeError(
              t,
              messages.failure,
              t("states.noPremiseOpen"),
              messages.failureParams
            )
          );
          return false;
        }
        try {
          const stored = await foyerApi.putPremise(premiseId, update(base));
          if (premiseIdRef.current === premiseId) {
            configRef.current = stored;
            applySaved(stored);
          }
          toast.success(messages.success);
          return true;
        } catch (err) {
          toast.error(
            describeError(t, messages.failure, err, messages.failureParams)
          );
          return false;
        }
      };
      const result = commitQueue.current.then(run, run);
      commitQueue.current = result;
      return result;
    },
    [premiseId, applySaved, toast, t]
  );

  const applyServerChange = useCallback(
    (update: (current: PremiseConfig) => PremiseConfig) => {
      const base = configRef.current;
      if (!base) return;
      const next = update(base);
      configRef.current = next;
      applySaved(next);
    },
    [applySaved]
  );

  const value = useMemo<PremiseWorkspaceState>(
    () => ({
      premiseId,
      // Never a previous location's config while the new one is loading.
      premise:
        premiseId && config && config.premiseId === premiseId ? config : null,
      loading: !!premiseId && loading,
      error,
      commit,
      applyServerChange,
    }),
    [premiseId, config, loading, error, commit, applyServerChange]
  );

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function usePremiseWorkspace(): PremiseWorkspaceState {
  const ctx = useContext(Ctx);
  if (!ctx) {
    throw new Error("usePremiseWorkspace must be used inside the CMS shell");
  }
  return ctx;
}
